import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from "react";
import type { EmojiClickData } from "emoji-picker-react";
import styled from "styled-components";
import { ConversationDetailsContext } from "../../context";
import { EmojiPicker } from "./emoji-picker";
import { EmojiIcon } from "./emoji-icon";
const EmojiPickerContainer = styled.div`
  position: relative;
`;

const EmojiButton = styled.button`
  padding: 8px 12px;
  background-color: transparent;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  min-width: 40px;
  height: 40px;

  &:hover {
    background-color: #ebf6ff;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background-color: #f5f5f5;

    svg path {
      stroke: #a0a0a0;
    }
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const EmojiPickerPopup = styled.div`
  position: absolute;
  z-index: 10000;
  bottom: 50px;
  left: 0;
`;

export interface TextMessageEmojiButtonProps {
  /** Callback when emoji is selected — receives the emoji character only */
  onEmojiSelect: (emoji: string) => void;
  /** Whether the picker is disabled */
  disabled?: boolean;
  /** Test ID for testing purposes */
  "data-testid"?: string;
}

/**
 * Emoji Picker component for TextArea
 * Displays below the text input and appends emojis to the text value
 */
export const TextMessageEmojiButton: React.FC<TextMessageEmojiButtonProps> = ({
  onEmojiSelect,
  disabled = false,
  "data-testid": testId = "text-emoji-picker"
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { setEmojiPickerError } = useContext(ConversationDetailsContext);

  const handleEmojiClick = useCallback(
    (emojiData: EmojiClickData) => {
      try {
        const emoji = emojiData.emoji;
        onEmojiSelect(emoji);

        // Clear any previous errors on successful selection
        setEmojiPickerError(false);

        // Close the picker
        setIsOpen(false);
      } catch (error) {
        setEmojiPickerError(true);
        setIsOpen(false);
      }
    },
    [onEmojiSelect, setEmojiPickerError]
  );

  const togglePicker = () => {
    if (!disabled) {
      setIsOpen(prev => !prev);
    }
  };

  // Close picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        const emojiPicker = document.querySelector(".EmojiPickerReact");
        if (emojiPicker && !emojiPicker.contains(event.target as Node)) {
          setIsOpen(false);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <EmojiPickerContainer data-testid={testId}>
      <EmojiButton
        aria-label="Add Emoji"
        data-testid={`${testId}-button`}
        disabled={disabled}
        ref={buttonRef}
        title="Add Emoji"
        type="button"
        onClick={togglePicker}
      >
        <EmojiIcon />
      </EmojiButton>
      {isOpen && (
        <EmojiPickerPopup data-testid={`${testId}-popup`}>
          <EmojiPicker
            data-testid={`${testId}-shared`}
            height={400}
            width={350}
            onEmojiClick={handleEmojiClick}
          />
        </EmojiPickerPopup>
      )}
    </EmojiPickerContainer>
  );
};

export default TextMessageEmojiButton;
